'use client';

import {useEffect, useRef, useState} from 'react';
import Image from 'next/image';
import {ChevronLeft, ChevronRight} from 'lucide-react';

type ImageCarouselProps = {
    images: string[];
    alt: string;
    isRtl: boolean;
};

export default function ImageCarousel({images, alt, isRtl}: ImageCarouselProps) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const AUTOPLAY_INTERVAL = 4000;

    const nextSlide = () => setCurrentSlide((p) => (p + 1) % images.length);
    const prevSlide = () => setCurrentSlide((p) => (p - 1 + images.length) % images.length);

    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        if (images.length > 1 && !isPaused) {
            intervalRef.current = setInterval(() => {
                setCurrentSlide((p) => (p + 1) % images.length);
            }, AUTOPLAY_INTERVAL);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [currentSlide, images.length, isPaused]);

    return (
        <div
            className="relative w-full h-72 rounded-2xl shadow-xl overflow-hidden bg-white"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            {images.map((src, idx) => (
                <div
                    key={src}
                    className="absolute inset-0"
                    style={{
                        transform: (() => {
                            const slideOffset = idx - currentSlide;
                            if (slideOffset === 0) return 'translateX(0%)';
                            if (slideOffset > 0) {
                                return isRtl ? 'translateX(-100%)' : 'translateX(100%)';
                            }
                            return isRtl ? 'translateX(100%)' : 'translateX(-100%)';
                        })(),
                        transition: 'transform 500ms ease-in-out',
                        zIndex: idx === currentSlide ? 10 : 5 - Math.abs(idx - currentSlide),
                        pointerEvents: idx === currentSlide ? 'auto' : 'none'
                    }}
                >
                    <Image
                        src={src}
                        alt={alt}
                        fill
                        className="object-contain p-6"
                        priority={idx === 0}
                        fetchPriority={idx === 0 ? 'high' : undefined}
                    />
                </div>
            ))}

            {images.length > 1 && (
                <>
                    <button
                        onClick={prevSlide}
                        className={`absolute ${isRtl ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 bg-white/80 text-brand-orange p-2 rounded-full hover:bg-white transition-colors z-20 shadow-md`}
                        aria-label="Previous"
                    >
                        {isRtl ? <ChevronRight size={18}/> : <ChevronLeft size={18}/>}
                    </button>
                    <button
                        onClick={nextSlide}
                        className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 bg-white/80 text-brand-orange p-2 rounded-full hover:bg-white transition-colors z-20 shadow-md`}
                        aria-label="Next"
                    >
                        {isRtl ? <ChevronLeft size={18}/> : <ChevronRight size={18}/>}
                    </button>

                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                        {images.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => setCurrentSlide(idx)}
                                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                                    idx === currentSlide ? 'bg-brand-orange' : 'bg-white/70 hover:bg-white'
                                }`}
                                aria-label={`Go to slide ${idx + 1}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
